/**
 * Browser console diagnostic controls.
 * Routine output is disabled unless CLIENT_DEBUG_MODE is enabled; warnings,
 * errors, and the bounded card-action trace pass through redaction.
 */
let MoonfallConsolePolicy;

(function () {
  'use strict';

  if (typeof window === 'undefined' || MoonfallConsolePolicy) return;
  if (typeof console === 'undefined') return;

  var METHODS = ['log', 'info', 'debug', 'warn', 'error'];
  var MAX_STRING = 400;
  var MAX_SANITIZE_INPUT = 4000;
  var MAX_DEPTH = 3;
  var MAX_KEYS = 20;
  var MAX_ARRAY = 12;
  var original = {};

  METHODS.forEach(function (method) {
    if (typeof console[method] === 'function') original[method] = console[method];
  });

  function sanitizeString(value, preserveLength) {
    var text = String(value == null ? '' : value);
    if (!preserveLength) text = text.slice(0, MAX_SANITIZE_INPUT);
    var sanitized = text
      .replace(/\/(Users|home)\/[^/\s]+/g, '/$1/[redacted-user]')
      .replace(/\b[A-Z]:\\Users\\[^\\\s]+/gi, '[redacted-home]')
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
      .replace(/\beyJ[a-zA-Z0-9._-]{10,}\b/g, '[redacted-token]')
      .replace(/\b(?:sk|rk)_(?:test|live)_[A-Za-z0-9_]{4,}\b/g, '[redacted-secret]')
      .replace(/\bwhsec_[A-Za-z0-9_]{4,}\b/g, '[redacted-secret]')
      .replace(/\b(?:cus|acct|sub|in|pi|seti|pm|ch|re|dp)_[A-Za-z0-9_]{6,}\b/g, '[redacted-billing-id]')
      .replace(/\b(?:db|postgres(?:ql)?|redis|mongodb(?:\+srv)?):\/\/[^\s]+/gi, '[redacted-service-url]')
      .replace(/\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+\/-]{12,}={0,2}\b/gi, '[redacted-token]')
      .replace(/\b(IP(?: address)?\s*[:=]?\s*)[A-F0-9:.]{3,}\b/gi, '$1[redacted-ip]')
      .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[redacted-ip]')
      .replace(/(["']?(?:firebaseuid|playerid|gameid|matchid|stableid|roomid|socketid|sessionid|connectionid|customerid|accountid|profileid|userid)["']?\s*[:=]\s*)["']?[A-Za-z0-9._-]{3,}["']?/gi, '$1[redacted-id]')
      .replace(/(["']?(?:access[_-]?token|auth(?:orization)?|cookie|credential|id[_-]?token|password|secret|token|api[_-]?key|session)["']?\s*[:=]\s*)["']?[^"'\s,;}]+["']?/gi, '$1[redacted]')
      .replace(/\b((?:(?:firebase|convex)\s+)?user\s+(?:signed\s+in|synced|authenticated)\s*[:=]\s*)["']?[^\s,;"']{3,}["']?/gi, '$1[redacted-id]')
      .replace(/\b((?:firebase(?:uid)?|player(?:id)?|game(?:id)?|match(?:id)?|stable(?:id)?|room(?:id)?|socket(?:id)?|session(?:id)?|connection(?:id)?|customer(?:id)?|account(?:id)?|profile(?:id)?|user(?:id)?)\s*(?:[:=]\s*|\s+(?!(?:signed|synced|authenticated)\b)))["']?[A-Za-z0-9._-]{6,}["']?/gi, '$1[redacted-id]')
      .replace(/([?&](?:access[_-]?token|auth[_-]?token|id[_-]?token|token|secret|api[_-]?key|session|code|state|signature|sig|policy|key-pair-id|x-(?:goog|amz)-[a-z0-9_-]+)=)[^&#\s]+/gi, '$1[redacted]')
      .replace(/\b((?:access[_-]?token|auth(?:orization)?|cookie|credential|id[_-]?token|password|secret|token|api[_-]?key|session)\s*[:=]\s*)[^\s,;]+/gi, '$1[redacted]');
    return preserveLength ? sanitized : sanitized.slice(0, MAX_STRING);
  }

  function blockedKey(key) {
    var normalized = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return /(?:authorization|cookie|credential|password|secret|token|apikey|privatekey|prompt|headers|body|email)/.test(normalized)
      || /(?:gamestate|gamepayload|deckconfig|imageurls?)/.test(normalized)
      || /^(?:hand|deck|story)/.test(normalized)
      || /(?:players|users|urls?|uris?)$/.test(normalized)
      || /^(?:game|gamestate|players|player|opponent|deck|hand|story|profile|user|userid|username|profileid|firebaseuid|playerid|roomid|socketid|sessionid|connectionid|stripecustomerid|customerid|accountid|matchid|ip|ipaddress|remoteaddress)$/.test(normalized);
  }

  function errorField(error, key, fallback) {
    try {
      return error[key] || fallback;
    } catch (_) {
      return fallback;
    }
  }

  function sanitizeValue(value, depth, seen) {
    if (value == null) return value;
    if (typeof value === 'string') return sanitizeString(value);
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    if (typeof value !== 'object' || depth > MAX_DEPTH) return undefined;
    if (seen.has(value)) return undefined;
    seen.add(value);

    if (value instanceof Error) {
      return {
        name: sanitizeString(errorField(value, 'name', 'Error')),
        message: sanitizeString(errorField(value, 'message', 'Unknown error')),
      };
    }

    if (Array.isArray(value)) {
      return value.slice(0, MAX_ARRAY)
        .map(function (item) { return sanitizeValue(item, depth + 1, seen); })
        .filter(function (item) { return item !== undefined; });
    }

    var output = {};
    var keys;
    try {
      keys = Object.keys(value).slice(0, MAX_KEYS);
    } catch (_) {
      return output;
    }
    keys.forEach(function (key) {
      if (blockedKey(key)) return;
      try {
        var sanitized = sanitizeValue(value[key], depth + 1, seen);
        if (sanitized !== undefined) output[key] = sanitized;
      } catch (_) {
        // Diagnostics must never execute a throwing getter twice.
      }
    });
    return output;
  }

  function sanitizeArguments(args, preserveStringLength) {
    var seen = new WeakSet();
    return Array.prototype.map.call(args, function (value) {
      try {
        if (preserveStringLength && typeof value === 'string') {
          return sanitizeString(value, true);
        }
        return sanitizeValue(value, 0, seen);
      } catch (_) {
        return '[unavailable-diagnostic]';
      }
    }).filter(function (value) { return value !== undefined; });
  }

  function isCardActionTrace(args) {
    var first = args && args[0];
    return typeof first === 'string' && (
      first.indexOf('[CardActionTrace] ') === 0 ||
      first.indexOf('[CardActionTraceDump] ') === 0
    );
  }

  function forward(method, args, preserveStringLength) {
    var target = original[method] || original.log;
    if (!target) return;
    target.apply(console, sanitizeArguments(args, preserveStringLength));
  }

  console.log = function () {
    if (window.CLIENT_DEBUG_MODE === true) forward('log', arguments);
  };
  console.info = function () {
    if (isCardActionTrace(arguments)) {
      forward('info', arguments, true);
    } else if (window.CLIENT_DEBUG_MODE === true) {
      forward('info', arguments);
    }
  };
  console.debug = function () {
    if (window.CLIENT_DEBUG_MODE === true) forward('debug', arguments);
  };
  console.warn = function () { forward('warn', arguments); };
  console.error = function () { forward('error', arguments); };

  MoonfallConsolePolicy = {
    restore: function () {
      METHODS.forEach(function (method) {
        if (original[method]) console[method] = original[method];
      });
    },
  };
})();

export { MoonfallConsolePolicy };
